// Adaptive scheduling engine. Pure functions over an event list + the current TCB
// age band. No React, no Supabase — just inputs in, predictions/flags out.
//
// Design notes (mirrors /root/.claude/plans/okay-we-are-going-jolly-sutherland.md):
//
// 1. Predictions blend a TCB midpoint prior with the baby's rolling personal median
//    via a weight = clamp(samples / SATURATE, 0, 1). Once we have >= SATURATE samples
//    the personal median dominates; below that, the TCB prior carries.
//
// 2. Night-stretch target uses "best baseline" (longest stretch the baby has done
//    recently and consistently) once we have data; otherwise falls back to the TCB
//    "= week# hours" rule.
//
// 3. Deviation flags are rule-based and phase-aware. We emit at most one Hint to the
//    home screen; severity ordering lives in `rankHints`.

import type { AgeBand } from "../data/tcbSchedule";
import { ageFromBirthday } from "./age";
import { en } from "./i18n/en";
import type { Strings } from "./i18n";
import {
  EVENT_COLUMNS as _EVENT_COLUMNS, // unused but keeps import graph honest
  sleepMinutesInWindow,
  startOfLocalDay,
  type EventRow,
} from "./events";

void _EVENT_COLUMNS;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type Basis = "tcb" | "blended" | "personal" | "best-baseline";

export type FeedPrediction = {
  at: Date;
  rangeMin: Date;
  rangeMax: Date;
  amountOzMin?: number;
  amountOzMax?: number;
  amountOzMid?: number;
  confidence: "low" | "med" | "high";
  basis: Basis;
  note?: string;
};

export type NapPrediction = {
  at: Date;
  rangeMin: Date;
  rangeMax: Date;
  confidence: "low" | "med" | "high";
  basis: Basis;
  note?: string;
};

export type BestBaseline = {
  hours: number;
  nightsSampled: number;
};

export type Hint = {
  id: string;
  severity: 1 | 2 | 3 | 4 | 5; // 1 = critical (overdue feed), 5 = nice-to-know
  title: string;
  detail?: string;
};

export type TempGroup = "group-1" | "group-2" | "observing";

export type ReverseCyclingSignal = {
  nightShareOfFeeds: number; // 0..1, share of feeding *events* (bottle + nurse) at night
  flagged: boolean;
};

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const SATURATE_FEEDS = 20;
const SATURATE_NAPS = 10;
const ROLLING_WINDOW_DAYS = 7;
// Reverse cycling only makes sense once a day/night rhythm is expected. Before
// ~8 weeks (corrected), heavy night feeding is normal newborn circadian
// immaturity, not a pattern to correct — flagging it there is just noise.
const REVERSE_CYCLING_MIN_DAYS = 56;
// We judge reverse cycling by the share of feeding *events* at night, not oz:
// nursing has no logged oz, so summing oz silently drops daytime breastfeeds
// and makes any nursed baby look night-heavy. Day and night are equal 12h
// windows, so an even feeder sits near 0.5 — we only flag a clear night
// majority. Needs a real sample first so a sparse log can't trip it.
const REVERSE_CYCLING_NIGHT_SHARE = 0.55;
const REVERSE_CYCLING_MIN_FEEDS = 14;
const CLUSTER_WINDOW_START_HOUR = 16;
const CLUSTER_WINDOW_END_HOUR = 22;
const CLUSTER_MAX_GAP_MIN = 120;

// ---------------------------------------------------------------------------
// Generic helpers
// ---------------------------------------------------------------------------

function median(xs: number[]): number {
  if (xs.length === 0) return 0;
  const sorted = [...xs].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
}

function percentile(xs: number[], p: number): number {
  if (xs.length === 0) return 0;
  const sorted = [...xs].sort((a, b) => a - b);
  const idx = Math.min(sorted.length - 1, Math.max(0, Math.round((p / 100) * (sorted.length - 1))));
  return sorted[idx];
}

function clamp(x: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, x));
}

function round1(x: number): number {
  return Math.round(x * 10) / 10;
}

function ms(hours: number): number {
  return hours * 60 * 60 * 1000;
}

function withinWindow(date: Date, sinceMs: number, now: Date = new Date()): boolean {
  return now.getTime() - date.getTime() <= sinceMs;
}

// ---------------------------------------------------------------------------
// Cluster-feed window
// ---------------------------------------------------------------------------

export function isClusterWindow(now: Date = new Date()): boolean {
  const h = now.getHours();
  return h >= CLUSTER_WINDOW_START_HOUR && h < CLUSTER_WINDOW_END_HOUR;
}

function isInClusterMode(events: EventRow[], now: Date = new Date()): boolean {
  if (!isClusterWindow(now)) return false;
  const recentFeeds = events
    .filter((e) => e.type === "feed" || e.type === "nurse")
    .filter((e) => withinWindow(new Date(e.at), ms(2.5), now))
    .sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime());
  if (recentFeeds.length < 2) return false;
  const gapMin =
    (new Date(recentFeeds[0].at).getTime() - new Date(recentFeeds[1].at).getTime()) / 60_000;
  return gapMin <= CLUSTER_MAX_GAP_MIN;
}

// ---------------------------------------------------------------------------
// Personal stats
// ---------------------------------------------------------------------------

function recentFeedIntervalsHours(events: EventRow[], now: Date = new Date()): number[] {
  const cutoff = now.getTime() - ROLLING_WINDOW_DAYS * 86_400_000;
  const feeds = events
    .filter((e) => (e.type === "feed" || e.type === "nurse") && new Date(e.at).getTime() >= cutoff)
    .sort((a, b) => new Date(a.at).getTime() - new Date(b.at).getTime());

  const intervals: number[] = [];
  for (let i = 1; i < feeds.length; i++) {
    const hrs =
      (new Date(feeds[i].at).getTime() - new Date(feeds[i - 1].at).getTime()) / 3_600_000;
    // Drop intervals > 12h — those are night stretches, not "between feeds" cadence.
    if (hrs > 0 && hrs <= 12) intervals.push(hrs);
  }
  return intervals;
}

function recentFeedOz(events: EventRow[], now: Date = new Date()): number[] {
  const cutoff = now.getTime() - ROLLING_WINDOW_DAYS * 86_400_000;
  return events
    .filter(
      (e) =>
        e.type === "feed" &&
        e.amount_oz != null &&
        e.amount_oz > 0 &&
        new Date(e.at).getTime() >= cutoff,
    )
    .map((e) => e.amount_oz!);
}

function recentWakeWindowsMin(events: EventRow[], now: Date = new Date()): number[] {
  // For each pair of (sleep_end, next sleep_start), the awake duration.
  const cutoff = now.getTime() - ROLLING_WINDOW_DAYS * 86_400_000;
  const sleeps = events
    .filter(
      (e) =>
        e.type === "sleep" &&
        e.end_at != null &&
        new Date(e.at).getTime() >= cutoff,
    )
    .sort((a, b) => new Date(a.at).getTime() - new Date(b.at).getTime());

  const windows: number[] = [];
  for (let i = 0; i < sleeps.length - 1; i++) {
    const wakeAt = new Date(sleeps[i].end_at!).getTime();
    const nextSleep = new Date(sleeps[i + 1].at).getTime();
    const minutes = (nextSleep - wakeAt) / 60_000;
    // Drop > 4h — probably an unlogged nap.
    const wake = new Date(wakeAt);
    const next = new Date(nextSleep);
    // Learn daytime wake windows, never short overnight feeding/resettling gaps.
    if (wake.getHours() >= 7 && next.getHours() < 19 &&
        wake.toDateString() === next.toDateString() && nextSleep <= now.getTime() &&
        minutes > 0 && minutes <= 240) windows.push(minutes);
  }
  return windows;
}

// ---------------------------------------------------------------------------
// Predictions
// ---------------------------------------------------------------------------

export function predictNextFeed(
  events: EventRow[],
  band: AgeBand,
  now: Date = new Date(),
): FeedPrediction | null {
  const lastFeed = events
    .filter((e) => e.type === "feed" || e.type === "nurse")
    .sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime())[0];
  if (!lastFeed) return null;

  const intervals = recentFeedIntervalsHours(events, now);
  const feedOz = recentFeedOz(events, now);

  const tcbMidHrs = (band.feedIntervalHrsMin + band.feedIntervalHrsMax) / 2;
  const personalMedianHrs = intervals.length ? median(intervals) : tcbMidHrs;
  const wFeed = clamp(intervals.length / SATURATE_FEEDS, 0, 1);
  const blendedHrs = wFeed * personalMedianHrs + (1 - wFeed) * tcbMidHrs;

  const clusterMode = isInClusterMode(events, now);
  const finalHrs = clusterMode
    ? Math.max(1.25, Math.min(2, blendedHrs))
    : blendedHrs;

  const nextAt = new Date(new Date(lastFeed.at).getTime() + finalHrs * 3_600_000);

  // Range from personal P25–P75 when we have data, otherwise +/- 25% of the prediction.
  let rangeMin: Date;
  let rangeMax: Date;
  if (intervals.length >= 4) {
    rangeMin = new Date(new Date(lastFeed.at).getTime() + percentile(intervals, 25) * 3_600_000);
    rangeMax = new Date(new Date(lastFeed.at).getTime() + percentile(intervals, 75) * 3_600_000);
  } else {
    rangeMin = new Date(nextAt.getTime() - finalHrs * 0.25 * 3_600_000);
    rangeMax = new Date(nextAt.getTime() + finalHrs * 0.25 * 3_600_000);
  }

  // Oz prediction
  let amountOzMid: number | undefined;
  let amountOzMin: number | undefined;
  let amountOzMax: number | undefined;
  if (feedOz.length >= 5) {
    amountOzMid = median(feedOz);
    amountOzMin = percentile(feedOz, 25);
    amountOzMax = percentile(feedOz, 75);
  } else if (band.feedsPerDayMin && band.feedsPerDayMax) {
    // No personal oz data — leave as range only if TCB has a hint.
    amountOzMid = undefined;
  }

  const confidence: FeedPrediction["confidence"] =
    wFeed >= 0.7 ? "high" : wFeed >= 0.3 ? "med" : "low";
  const basis: Basis =
    clusterMode ? "blended" : wFeed >= 0.7 ? "personal" : wFeed >= 0.3 ? "blended" : "tcb";

  let note: string | undefined;
  if (clusterMode) note = "Cluster feeding — shorter gaps are normal until bedtime.";
  else if (basis === "personal") note = "Based on your baby's last 7 days.";
  else if (basis === "tcb") note = `Typical pace for ${band.label}.`;

  return { at: nextAt, rangeMin, rangeMax, amountOzMid, amountOzMin, amountOzMax, confidence, basis, note };
}

export function predictNextNap(
  events: EventRow[],
  band: AgeBand,
  now: Date = new Date(),
): NapPrediction | null {
  const lastSleep = events
    .filter((e) => e.type === "sleep" && new Date(e.at).getTime() <= now.getTime())
    .sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime())[0];
  if (!lastSleep) return null;
  if (lastSleep.end_at == null || new Date(lastSleep.end_at).getTime() > now.getTime()) {
    // Currently asleep.
    return null;
  }

  const windows = recentWakeWindowsMin(events, now);
  const tcbMidMin = (band.wakeWindowMin + band.wakeWindowMax) / 2;
  const personalMedianMin = windows.length ? median(windows) : tcbMidMin;
  // Retain an age prior even with abundant history; logs describe what happened,
  // not necessarily an appropriate target. Keep the target inside its shown range.
  const wNap = Math.min(0.7, clamp(windows.length / SATURATE_NAPS, 0, 1));
  const blendedMin = clamp(
    wNap * personalMedianMin + (1 - wNap) * tcbMidMin,
    band.wakeWindowMin, band.wakeWindowMax,
  );

  const wakeAt = new Date(lastSleep.end_at).getTime();
  if (startOfLocalDay(new Date(wakeAt)).getTime() !== startOfLocalDay(now).getTime()) return null;
  const nextAt = new Date(wakeAt + blendedMin * 60_000);
  const rangeMin = new Date(wakeAt + band.wakeWindowMin * 60_000);
  const rangeMax = new Date(wakeAt + band.wakeWindowMax * 60_000);

  const confidence: NapPrediction["confidence"] =
    wNap >= 0.7 ? "high" : wNap >= 0.3 ? "med" : "low";
  const basis: Basis = wNap >= 0.7 ? "personal" : wNap >= 0.3 ? "blended" : "tcb";

  return {
    at: nextAt,
    rangeMin,
    rangeMax,
    confidence,
    basis,
    note: basis === "personal" ? "Based on your baby's typical wake window." : undefined,
  };
}

// ---------------------------------------------------------------------------
// Best baseline (longest recent recurring night stretch)
// ---------------------------------------------------------------------------

function isNightSleep(e: EventRow): boolean {
  if (e.type !== "sleep" || e.end_at == null) return false;
  if (e.sleep_kind === "night") return true;
  const h = new Date(e.at).getHours();
  return h >= 19 || h < 7;
}

export function computeBestBaseline(
  events: EventRow[],
  windowNights = 7,
  now: Date = new Date(),
): BestBaseline | null {
  const cutoff = now.getTime() - windowNights * 86_400_000;
  const nights = events
    .filter(isNightSleep)
    .filter((e) => new Date(e.at).getTime() >= cutoff)
    .map((e) => (new Date(e.end_at!).getTime() - new Date(e.at).getTime()) / 3_600_000)
    .filter((h) => h > 0);

  if (nights.length < 3) return null;
  // "Best baseline" = the P50 (median) — i.e. what's recently *consistent*.
  return { hours: Math.round(median(nights) * 10) / 10, nightsSampled: nights.length };
}

// ---------------------------------------------------------------------------
// Temperament group inference
// ---------------------------------------------------------------------------

export function inferTempGroup(events: EventRow[], now: Date = new Date()): TempGroup {
  // Look at the last 5 evenings (18:00–23:00).
  const dayStart = new Date(now);
  dayStart.setHours(0, 0, 0, 0);
  const start = new Date(dayStart.getTime() - 5 * 86_400_000);

  let nightsWithData = 0;
  let earlyBedtimeCount = 0; // Group 1 signal
  let lateBedtimeCount = 0; // Group 2 signal
  let clusterFeedNights = 0; // Group 2 signal

  for (let d = 0; d < 5; d++) {
    const dayMid = new Date(start.getTime() + d * 86_400_000);
    const eveStart = new Date(dayMid);
    eveStart.setHours(18, 0, 0, 0);
    const eveEnd = new Date(dayMid);
    eveEnd.setHours(23, 0, 0, 0);

    const eveSleeps = events.filter(
      (e) =>
        e.type === "sleep" &&
        new Date(e.at).getTime() >= eveStart.getTime() &&
        new Date(e.at).getTime() < eveEnd.getTime(),
    );
    const eveFeeds = events.filter(
      (e) =>
        (e.type === "feed" || e.type === "nurse") &&
        new Date(e.at).getTime() >= eveStart.getTime() &&
        new Date(e.at).getTime() < eveEnd.getTime(),
    );

    if (eveSleeps.length === 0 && eveFeeds.length === 0) continue;
    nightsWithData++;

    // Bedtime = first sleep event that runs past midnight, or last sleep_kind=night.
    const bedtimeSleep =
      eveSleeps.find((s) => s.end_at == null || new Date(s.end_at).getHours() < 7) ??
      eveSleeps.find((s) => s.sleep_kind === "night");
    if (bedtimeSleep) {
      const h = new Date(bedtimeSleep.at).getHours();
      if (h <= 20) earlyBedtimeCount++;
      else if (h >= 21) lateBedtimeCount++;
    }

    // Cluster nights: ≥ 3 feeds in 18:00–23:00 with avg gap <120min.
    if (eveFeeds.length >= 3) {
      const sorted = eveFeeds.sort((a, b) => new Date(a.at).getTime() - new Date(b.at).getTime());
      let gaps = 0;
      let smallGaps = 0;
      for (let i = 1; i < sorted.length; i++) {
        gaps++;
        const gap = (new Date(sorted[i].at).getTime() - new Date(sorted[i - 1].at).getTime()) / 60_000;
        if (gap <= 120) smallGaps++;
      }
      if (gaps > 0 && smallGaps / gaps >= 0.5) clusterFeedNights++;
    }
  }

  if (nightsWithData < 3) return "observing";

  // Simple decision: tally the signals.
  const group1Score = earlyBedtimeCount;
  const group2Score = lateBedtimeCount + clusterFeedNights;
  if (group1Score > group2Score) return "group-1";
  if (group2Score > group1Score) return "group-2";
  return "observing";
}

// ---------------------------------------------------------------------------
// Reverse-cycling detection
// ---------------------------------------------------------------------------

export function detectReverseCycling(
  events: EventRow[],
  now: Date = new Date(),
): ReverseCyclingSignal {
  const cutoff = now.getTime() - ROLLING_WINDOW_DAYS * 86_400_000;
  const feeds = events.filter(
    (e) =>
      (e.type === "feed" || e.type === "nurse") &&
      new Date(e.at).getTime() >= cutoff,
  );

  let dayFeeds = 0;
  let nightFeeds = 0;
  for (const e of feeds) {
    const h = new Date(e.at).getHours();
    if (h >= 7 && h < 19) dayFeeds++;
    else nightFeeds++;
  }
  const total = dayFeeds + nightFeeds;
  if (total < REVERSE_CYCLING_MIN_FEEDS) return { nightShareOfFeeds: 0, flagged: false };

  const share = nightFeeds / total;
  return { nightShareOfFeeds: share, flagged: share >= REVERSE_CYCLING_NIGHT_SHARE };
}

// ---------------------------------------------------------------------------
// Deviation flags
// ---------------------------------------------------------------------------

export function detectDeviations(
  events: EventRow[],
  band: AgeBand,
  baby: { birthday: string; due_date: string | null; pediatrician_cleared_stretching?: boolean },
  now: Date = new Date(),
  t: Strings = en,
): Hint[] {
  const out: Hint[] = [];
  const referenceDate = baby.due_date ?? baby.birthday;
  const age = ageFromBirthday(referenceDate, now);
  const isSupplyBuilding = band.phase === "supply-building" || age.totalDays <= 21;

  // Daytime-sleep cap — checked first so it surfaces even during a long nap.
  const ds = daytimeSleepStatus(events, band, now);
  if (ds.overCap && ds.capMin != null) {
    out.push({
      id: "daytime-sleep-cap-exceeded",
      severity: 2,
      title: t.hints.daySleepTitle,
      detail: t.hints.daySleepDetail(round1(ds.totalDaytimeMin / 60), round1(ds.capMin / 60)),
    });
  }

  // 1. Currently asleep — surface nothing further.
  const currentSleep = events.find((e) => e.type === "sleep" && e.end_at == null);
  if (currentSleep) {
    return rankHints(out);
  }

  // 2. Wake-window check (only if not in supply building).
  const lastSleep = events
    .filter((e) => e.type === "sleep" && e.end_at != null)
    .sort((a, b) => new Date(b.end_at!).getTime() - new Date(a.end_at!).getTime())[0];
  if (lastSleep && !isSupplyBuilding) {
    const awakeMin = (now.getTime() - new Date(lastSleep.end_at!).getTime()) / 60_000;
    if (awakeMin > band.wakeWindowMax) {
      out.push({
        id: "wake-window-exceeded",
        severity: 2,
        title: t.hints.wakeWindowExceededTitle,
        detail: t.hints.wakeWindowExceededDetail(Math.round(awakeMin), band.wakeWindowMax),
      });
    } else if (awakeMin > band.wakeWindowMin && awakeMin > band.wakeWindowMax * 0.85) {
      out.push({
        id: "wake-window-near",
        severity: 4,
        title: t.hints.wakeWindowNearTitle,
        detail: t.hints.wakeWindowNearDetail(Math.round(awakeMin), band.wakeWindowMin, band.wakeWindowMax),
      });
    }
  }

  // 3. Feed cadence — daytime only. Overnight, a long gap IS the goal (the
  // night stretch), so nagging "time for a feed" at 2am is wrong; hungry
  // babies wake themselves. Day window matches the 7:00–19:00 convention
  // used everywhere else in this file.
  const hour = now.getHours();
  const isDaytime = hour >= 7 && hour < 19;
  const lastFeed = events
    .filter((e) => e.type === "feed" || e.type === "nurse")
    .sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime())[0];
  if (lastFeed && isDaytime) {
    const sinceFeedHrs = (now.getTime() - new Date(lastFeed.at).getTime()) / 3_600_000;
    if (!isSupplyBuilding && sinceFeedHrs > band.feedIntervalHrsMax) {
      out.push({
        id: "feed-overdue-hard",
        severity: 1,
        title: t.hints.feedOverdueHardTitle,
        detail: t.hints.feedOverdueHardDetail(sinceFeedHrs.toFixed(1), band.feedIntervalHrsMax),
      });
    } else {
      const intervals = recentFeedIntervalsHours(events, now);
      if (intervals.length >= 4) {
        const p90 = percentile(intervals, 90);
        if (sinceFeedHrs > p90) {
          out.push({
            id: "feed-overdue-personal",
            severity: 3,
            title: t.hints.feedOverduePersonalTitle,
            detail: t.hints.feedOverduePersonalDetail(sinceFeedHrs.toFixed(1), p90.toFixed(1)),
          });
        }
      }
    }
  }

  // 4. Cluster window hint.
  if (isInClusterMode(events, now)) {
    out.push({
      id: "cluster-mode",
      severity: 4,
      title: t.hints.clusterTitle,
      detail: t.hints.clusterDetail,
    });
  }

  // 5. Catnap suggestion.
  const cat = suggestCatnap(events, band, now);
  if (cat.needed) {
    out.push({
      id: "catnap-suggestion",
      severity: 4,
      title: t.hints.catnapTitle,
      detail: t.hints.catnapDetail(
        cat.minutesSinceLastNapEnd ?? 0,
        `${band.bedtimeEarliest}–${band.bedtimeLatest}`,
      ),
    });
  }

  // 6. Reverse cycling — only once a day/night rhythm is developmentally
  // expected (see REVERSE_CYCLING_MIN_DAYS). For younger babies, night-heavy
  // intake is normal and "shift calories to the day" is bad advice.
  if (!isSupplyBuilding && age.totalDays >= REVERSE_CYCLING_MIN_DAYS) {
    const rc = detectReverseCycling(events, now);
    if (rc.flagged) {
      out.push({
        id: "reverse-cycling",
        severity: 2,
        title: t.hints.reverseCyclingTitle,
        detail: t.hints.reverseCyclingDetail(Math.round(rc.nightShareOfFeeds * 100)),
      });
    }
  }

  // 7. R.A.B. risk on the last feed.
  const rabFlag = detectRAB(events, band, now, t);
  if (rabFlag) out.push(rabFlag);

  // 8. Night-stretch progression — only once stretching applies and is cleared.
  if (!isSupplyBuilding && band.nightStretchTargetHours != null && baby.pediatrician_cleared_stretching) {
    const ns = nightStretchProgress(events, band, now);
    if (ns.onPace === false && ns.sevenDayMedianHrs != null) {
      out.push({
        id: "night-stretch-behind",
        severity: 3,
        title: t.hints.nightStretchBehindTitle,
        detail: t.hints.nightStretchBehindDetail(ns.sevenDayMedianHrs, ns.targetHrs ?? 0),
      });
    }
  }

  // 9. Night-sleep regression.
  const reg = detectRegression(events, band, now, t);
  if (reg) out.push(reg);

  return rankHints(out);
}

export function rankHints(hints: Hint[]): Hint[] {
  return [...hints].sort((a, b) => a.severity - b.severity);
}

export function topHint(hints: Hint[]): Hint | null {
  const ranked = rankHints(hints);
  return ranked[0] ?? null;
}

// ---------------------------------------------------------------------------
// Catnap suggestion
// ---------------------------------------------------------------------------

export function suggestCatnap(
  events: EventRow[],
  band: AgeBand,
  now: Date = new Date(),
): { needed: boolean; minutesSinceLastNapEnd?: number } {
  // Only meaningful in the late afternoon, before bedtime, and only if we have a recent nap.
  const hour = now.getHours();
  if (hour < 15 || hour >= 19) return { needed: false };

  const lastNapEnd = events
    .filter((e) => e.type === "sleep" && e.end_at != null && e.sleep_kind !== "night")
    .sort((a, b) => new Date(b.end_at!).getTime() - new Date(a.end_at!).getTime())[0];
  if (!lastNapEnd) return { needed: false };

  const sinceMin = (now.getTime() - new Date(lastNapEnd.end_at!).getTime()) / 60_000;
  // If last nap ended more than 2.5h before earliest bedtime, suggest a catnap.
  const [bedH, bedM] = band.bedtimeEarliest.split(":").map(Number);
  const earliestBedtime = new Date(now);
  earliestBedtime.setHours(bedH, bedM, 0, 0);
  const minutesUntilBedtime = (earliestBedtime.getTime() - now.getTime()) / 60_000;

  if (minutesUntilBedtime > 60 && sinceMin > 90) {
    return { needed: true, minutesSinceLastNapEnd: Math.round(sinceMin) };
  }
  return { needed: false };
}

// ---------------------------------------------------------------------------
// Rest-At-Breast (R.A.B.) detection
// ---------------------------------------------------------------------------

export function detectRAB(
  events: EventRow[],
  _band: AgeBand,
  now: Date = new Date(),
  t: Strings = en,
): Hint | null {
  // Look at the last feed. If its duration is > 2× the rolling median for similar
  // (daytime) feeds, flag it.
  const lastFeed = events
    .filter((e) => e.type === "nurse" && e.end_at != null)
    .sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime())[0];
  if (!lastFeed || !lastFeed.end_at) return null;

  const lastDuration = (new Date(lastFeed.end_at).getTime() - new Date(lastFeed.at).getTime()) / 60_000;

  const cutoff = now.getTime() - 7 * 86_400_000;
  const recent = events
    .filter((e) => e.type === "nurse" && e.end_at != null && new Date(e.at).getTime() >= cutoff)
    .map((e) => (new Date(e.end_at!).getTime() - new Date(e.at).getTime()) / 60_000)
    .filter((m) => m > 0);

  if (recent.length < 4) return null;
  const med = median(recent);

  if (lastDuration > med * 2 && lastDuration > 25) {
    return {
      id: "rab-risk",
      severity: 4,
      title: t.hints.rabTitle,
      detail: t.hints.rabDetail,
    };
  }
  return null;
}

// ---------------------------------------------------------------------------
// Personalized band overlay
// ---------------------------------------------------------------------------

export function personalizedBand(events: EventRow[], _band: AgeBand, now: Date = new Date()) {
  const intervals = recentFeedIntervalsHours(events, now);
  const wakeWindows = recentWakeWindowsMin(events, now);
  const feedOz = recentFeedOz(events, now);

  return {
    feedIntervalMedianHrs: intervals.length ? median(intervals) : null,
    wakeWindowMedianMin: wakeWindows.length ? median(wakeWindows) : null,
    ozPerFeedMedian: feedOz.length ? median(feedOz) : null,
    intervalsSampled: intervals.length,
    wakeWindowsSampled: wakeWindows.length,
    feedsSampled: feedOz.length,
  };
}

// ---------------------------------------------------------------------------
// Daytime-sleep cap — over-long day sleep is the #1 thing that erodes night
// consolidation. Turns the band's nap-cap fields into live guidance.
// ---------------------------------------------------------------------------

/**
 * The day/night lines, in minutes from midnight. Sleep outside 7am–7pm is night
 * sleep no matter what `sleep_kind` says — the same boundaries the quick-log
 * buttons use to label a new sleep (`h >= 19 || h < 7` → night), and the ones
 * `longestNightStretch` runs between.
 */
export const DAY_SLEEP_START_MIN = 7 * 60;
export const DAY_SLEEP_END_MIN = 19 * 60;

export type DaytimeSleepStatus = {
  totalDaytimeMin: number;
  capMin: number | null;
  longestNapMin: number;
  napCapMin: number | null;
  overCap: boolean;
};

export function daytimeSleepStatus(
  events: EventRow[],
  band: AgeBand,
  now: Date = new Date(),
): DaytimeSleepStatus {
  const midnight = startOfLocalDay(now).getTime();
  // Day sleep is the 7am–7pm window. Anything outside it is night, however the
  // row is labelled — a 5am stretch is the tail of the night and an 8pm one is
  // the start of it, and counting either against the daytime cap made a normal
  // night look like too much day sleep. A sleep spanning a boundary contributes
  // only the minutes inside the window.
  const dayStart = midnight + DAY_SLEEP_START_MIN * 60_000;
  const dayEnd = midnight + DAY_SLEEP_END_MIN * 60_000;
  let totalMin = 0;
  let longestNapMin = 0;
  for (const e of events) {
    if (e.type !== "sleep" || e.sleep_kind === "night") continue;
    // Same interval rule as todayTotals: count the slice that falls inside the
    // day window, so a nap running past a boundary isn't dropped by its start.
    const min = sleepMinutesInWindow(e, dayStart, dayEnd, now);
    if (min <= 0) continue;
    totalMin += min;
    if (min > longestNapMin) longestNapMin = min;
  }
  const capMin = band.totalDaytimeSleepCapHrs != null ? band.totalDaytimeSleepCapHrs * 60 : null;
  return {
    totalDaytimeMin: Math.round(totalMin),
    capMin,
    longestNapMin: Math.round(longestNapMin),
    napCapMin: band.napCapMin,
    overCap: capMin != null && totalMin > capMin,
  };
}

/**
 * Day sleep logged on a calendar day — `daytimeSleepStatus`'s 7am–7pm
 * clipped-window rule, generalized to any day so the History and Past-day
 * views agree with the guidance strip. A sleep is attributed to the day its
 * `at` falls on, and contributes only its minutes inside that day's window.
 */
export function daytimeSleepMinutesForDay(
  events: EventRow[],
  day: Date,
  now: Date = new Date(),
): number {
  const midnight = startOfLocalDay(day).getTime();
  const dayStart = midnight + DAY_SLEEP_START_MIN * 60_000;
  const dayEnd = midnight + DAY_SLEEP_END_MIN * 60_000;
  let totalMin = 0;
  for (const e of events) {
    if (e.type !== "sleep" || e.sleep_kind === "night") continue;
    const at = new Date(e.at).getTime();
    if (at < midnight || at >= midnight + 86_400_000) continue;
    totalMin += sleepMinutesInWindow(e, dayStart, dayEnd, now);
  }
  return totalMin;
}

// ---------------------------------------------------------------------------
// Night-stretch progression — is the longest night stretch trending toward the
// age-appropriate goal? Drives the night-goal line and the "behind" hint.
// ---------------------------------------------------------------------------

export type NightStretchProgress = {
  targetHrs: number | null;
  lastNightHrs: number | null;
  sevenDayMedianHrs: number | null;
  trendHrsPerWeek: number | null;
  onPace: boolean | null;
};

function nightSleepHoursBetween(events: EventRow[], sinceMs: number, untilMs: number): number[] {
  return events
    .filter(isNightSleep)
    .filter((e) => {
      const t = new Date(e.at).getTime();
      return t >= sinceMs && t < untilMs;
    })
    .map((e) => (new Date(e.end_at!).getTime() - new Date(e.at).getTime()) / 3_600_000)
    .filter((h) => h > 0);
}

export function nightStretchProgress(
  events: EventRow[],
  band: AgeBand,
  now: Date = new Date(),
): NightStretchProgress {
  const recent = nightSleepHoursBetween(events, now.getTime() - 7 * 86_400_000, now.getTime());
  const prior = nightSleepHoursBetween(
    events,
    now.getTime() - 14 * 86_400_000,
    now.getTime() - 7 * 86_400_000,
  );
  const sevenDayMedianHrs = recent.length >= 3 ? round1(median(recent)) : null;
  const priorMedian = prior.length >= 3 ? median(prior) : null;
  const trendHrsPerWeek =
    sevenDayMedianHrs != null && priorMedian != null ? round1(sevenDayMedianHrs - priorMedian) : null;

  const lastNight = events
    .filter(isNightSleep)
    .sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime())[0];
  const lastNightHrs = lastNight
    ? round1((new Date(lastNight.end_at!).getTime() - new Date(lastNight.at).getTime()) / 3_600_000)
    : null;

  const targetHrs = band.nightStretchTargetHours;
  let onPace: boolean | null = null;
  if (sevenDayMedianHrs != null && targetHrs != null) {
    if (sevenDayMedianHrs >= targetHrs) onPace = true;
    else if (trendHrsPerWeek != null) onPace = trendHrsPerWeek >= band.weeklyStretchGainHrs * 0.5;
    else onPace = false;
  }
  return { targetHrs, lastNightHrs, sevenDayMedianHrs, trendHrsPerWeek, onPace };
}

// ---------------------------------------------------------------------------
// Longest gap between feeds — a plain fact, not a goal. Whatever she actually
// did is the number; nothing here is behind or ahead of anything.
// ---------------------------------------------------------------------------

export type FeedGap = {
  min: number;
  startAt: Date; // the earlier feed
  endAt: Date; // the feed that ended the gap
};

// Longest *completed* gap between two consecutive feeds in the trailing window.
// Overnight stretches count — unlike recentFeedIntervalsHours, which drops
// gaps > 12h because it's modelling daytime cadence. The in-progress gap since
// the last feed is deliberately excluded: this answers "how long did she go?",
// and the feed-cadence tile already covers "how long has it been?".
export function longestFeedGap(
  events: EventRow[],
  now: Date = new Date(),
  windowHrs = 24,
): FeedGap | null {
  const cutoff = now.getTime() - windowHrs * 3_600_000;
  const feeds = events
    .filter((e) => (e.type === "feed" || e.type === "nurse") && new Date(e.at).getTime() >= cutoff)
    .sort((a, b) => new Date(a.at).getTime() - new Date(b.at).getTime());

  let best: FeedGap | null = null;
  for (let i = 1; i < feeds.length; i++) {
    const startAt = new Date(feeds[i - 1].at);
    const endAt = new Date(feeds[i].at);
    const min = Math.round((endAt.getTime() - startAt.getTime()) / 60_000);
    if (min > 0 && (best == null || min > best.min)) best = { min, startAt, endAt };
  }
  return best;
}

// ---------------------------------------------------------------------------
// Night regression — a sudden, material drop in night sleep over the last
// couple of nights vs. the prior baseline.
// ---------------------------------------------------------------------------

export function detectRegression(
  events: EventRow[],
  band: AgeBand,
  _now: Date = new Date(),
  t: Strings = en,
): Hint | null {
  const nights = events
    .filter(isNightSleep)
    .sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime())
    .map((e) => (new Date(e.end_at!).getTime() - new Date(e.at).getTime()) / 3_600_000)
    .filter((h) => h > 0);
  if (nights.length < 5) return null;
  const recentMed = median(nights.slice(0, 2));
  const priorMed = median(nights.slice(2, 7));
  if (priorMed <= 0) return null;
  const drop = priorMed - recentMed;
  if (drop < 1.5 && drop / priorMed < 0.25) return null;

  const reframe = band.phase === "regression-prep" || band.phase === "regression-active";
  return {
    id: "night-regression",
    severity: 3,
    title: t.hints.regressionTitle,
    detail: reframe
      ? t.hints.regressionDetailReframe(round1(priorMed), round1(recentMed))
      : t.hints.regressionDetailPlain(round1(priorMed), round1(recentMed)),
  };
}

